import { ReactNode } from "react";
import { Box, styled } from "@mui/material";
import { ShieldCheck, RefreshCw, Headphones } from "lucide-react";
import { tokens } from "@styles/tokens";
import { AUTH_SPLIT_WIDTH } from "./authPrimitives";

/**
 * Trust badges shown below the login card — security, flexibility, and support
 * reassurance. Purely presentational.
 */
export function TrustBadges() {
  return (
    <Badges>
      <Badge
        icon={<ShieldCheck size={20} strokeWidth={1.75} />}
        title="Secure & Encrypted"
        caption="Your data is always safe"
      />
      <Badge
        icon={<RefreshCw size={20} strokeWidth={1.75} />}
        title="Cancel Anytime"
        caption="No long-term contracts"
      />
      <Badge
        icon={<Headphones size={20} strokeWidth={1.75} />}
        title="24/7 Support"
        caption="We're here to help"
      />
    </Badges>
  );
}

const Badge = ({
  icon,
  title,
  caption,
}: {
  icon: ReactNode;
  title: string;
  caption: string;
}) => (
  <BadgeCol>
    <BadgeIcon>{icon}</BadgeIcon>
    <BadgeTitle>{title}</BadgeTitle>
    <BadgeCaption>{caption}</BadgeCaption>
  </BadgeCol>
);

// Mobile-only — on desktop the brand panel's testimonial reassures instead.
const Badges = styled(Box)({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: tokens.space(2),
  padding: `${tokens.space(4)}px 0`,
  [`@media (min-width:${AUTH_SPLIT_WIDTH}px)`]: { display: "none" },
});

const BadgeCol = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: tokens.space(1),
});

const BadgeIcon = styled(Box)(({ theme }) => ({
  display: "grid",
  color: theme.skuey.brand[600],
  background: theme.skuey.brandSubtleBg,
  height: "2.5rem",
  width: "2.5rem",
  placeContent: "center",
  borderRadius: tokens.radius.xs,
}));

const BadgeTitle = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.tiny[0],
  fontWeight: tokens.font.weight.semibold,
  color: theme.skuey.ink,
}));

const BadgeCaption = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.micro[0],
  lineHeight: `${tokens.font.size.micro[1]}px`,
  color: theme.skuey.muted,
}));
